import { ProcedureCodeSet, procedureCodeSets } from "./ProcedureCodeSets";
import { EstimatedProcedure } from "../../../../services/verifyService/model/verification/EstimatedProcedure";

// Interface for matched code set result
export interface CodeSetMatch {
  name: string;
  cptCodes: string[];
  matchedOn: string[];
}

export class ProcedureCodeSetMatch {
  /**
   * Determines if there's a matching code set based on the provided procedure code
   *
   * @param cptCode - The CPT code to match against code sets
   * @returns A matching code set if found, or null if no match
   */
  findMatchByCode(cptCode: string): CodeSetMatch | null {
    for (const codeSet of procedureCodeSets) {
      if (codeSet.cptCodes.includes(cptCode)) {
        return {
          name: codeSet.name,
          cptCodes: codeSet.cptCodes,
          matchedOn: [cptCode]
        };
      }
    }
    return null;
  }
  
  /**
   * Returns a list of procedure code sets that match multiple CPT codes
   *
   * @param procedures - Current list of procedures to match against
   * @returns Array of matching code sets
   */
  findMatchesByProcedures(procedures: EstimatedProcedure[]): CodeSetMatch[] {
    const matches: CodeSetMatch[] = [];
    
    // Guard against null/undefined procedures
    if (!procedures || procedures.length === 0) {
      return matches;
    }
    
    // Get all procedure codes from current procedures
    const procCodes = procedures.map(p => p.code || "").filter(code => code !== "");
    
    // Check each code set
    procedureCodeSets.forEach(codeSet => {
      // Find which codes in this set match with the current procedures
      const matchedCodes = codeSet.cptCodes.filter(code => 
        procCodes.includes(code)
      );
      
      // Do we have at least one match?
      if (matchedCodes.length > 0) {
        // Important: Are there any existing codes that are not part of our predefined set?
        // We need to check if there are any CPT codes in the procedure list that don't belong
        // in the current code set that we're examining
        const codesNotInThisSet = procCodes.filter(code => !codeSet.cptCodes.includes(code));
        
        // Don't suggest anything if there are codes not in this set (which might belong to a different set)
        if (codesNotInThisSet.length === 0) {
          // Only suggest if not all codes from the set are already present
          if (matchedCodes.length < codeSet.cptCodes.length) {
            matches.push({
              name: codeSet.name,
              cptCodes: codeSet.cptCodes.filter(code => !matchedCodes.includes(code)), // Only include codes not already in the list
              matchedOn: matchedCodes
            });
          }
        } else {
          console.log(`Skipping suggestion for ${codeSet.name} because there are codes that don't belong to this set: ${codesNotInThisSet.join(', ')}`);
        }
      }
    });
    
    return matches;
  }
  
  /**
   * Returns procedure objects to be added for a matched code set
   *
   * @param match - The code set match
   * @param existingProcedures - Current list of procedures
   * @returns Array of procedures to add
   */
  getProceduresToAdd(match: CodeSetMatch, existingProcedures: EstimatedProcedure[]): EstimatedProcedure[] {
    // Guard against invalid input
    if (!match || !match.cptCodes || !Array.isArray(match.cptCodes)) {
      return [];
    }
    
    // Ensure existingProcedures is an array
    const procedures = existingProcedures || [];
    
    // Extract existing codes, filtering out undefined values
    const existingCodes = procedures.map(p => p.code || "").filter(code => code !== "");
    
    // Filter out codes that are already in the procedure list
    const codesToAdd = match.cptCodes.filter(code => !existingCodes.includes(code));
    
    // Create an array of procedures to add
    return codesToAdd.map(code => {
      // Look up the original code set to get information
      const codeSet = procedureCodeSets.find(set => set.cptCodes.includes(code));
      // Create estimated procedure with the code set name as part of the description
      return new EstimatedProcedure(
        code, 
        `${code} - Part of ${match.name || 'Procedure Code Set'}`
      );
    });
  }
}
